import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader, getRequestIP } from "@tanstack/react-start/server";
import { z } from "zod";
import { surveyResponseSchema, insertSurveyResponse } from "./survey-response.functions";

const nullableText = (max: number) => z.string().trim().max(max).nullable().optional();

const inquirySchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(255),
  phone: nullableText(40),
  price_range: nullableText(100),
  interest: nullableText(100),
  clinic_name: nullableText(150),
  treatments: z.array(z.string().max(100)).max(20).nullable().optional(),
  lowest_care_plan_price: nullableText(60),
  monthly_revenue: nullableText(60),
  biggest_challenge: nullableText(1500),
  lead_score: z.number().min(0).max(10).nullable().optional(),
  raw_average: z.number().min(0).max(5).nullable().optional(),
  questions_answered: z.number().int().min(0).max(50).nullable().optional(),
  scoring_version: z.string().max(20).nullable().optional(),
  message: nullableText(2000),
  utm_source: nullableText(200),
  utm_medium: nullableText(200),
  utm_campaign: nullableText(300),
  utm_content: nullableText(300),
  utm_term: nullableText(200),
  utm_campaign_id: nullableText(120),
  utm_adset_id: nullableText(120),
  utm_ad_id: nullableText(120),
  fbclid: nullableText(500),
  gclid: nullableText(500),
  landing_path: nullableText(1000),
  referrer: nullableText(1000),
  last_touch_at: z.string().datetime().nullable().optional(),
  first_utm_source: nullableText(200),
  first_utm_medium: nullableText(200),
  first_utm_campaign: nullableText(300),
  first_utm_content: nullableText(300),
  first_utm_term: nullableText(200),
  first_utm_campaign_id: nullableText(120),
  first_utm_adset_id: nullableText(120),
  first_utm_ad_id: nullableText(120),
  first_fbclid: nullableText(500),
  first_gclid: nullableText(500),
  first_landing_path: nullableText(1000),
  first_referrer: nullableText(1000),
  first_touch_at: z.string().datetime().nullable().optional(),
  fbp: nullableText(200),
  fbc: nullableText(500),
  meta_event_id: nullableText(100),
});

const attributionSchema = z
  .object({
    utm_source: z.string().max(300).nullable().optional(),
    utm_medium: z.string().max(300).nullable().optional(),
    utm_campaign: z.string().max(300).nullable().optional(),
    utm_content: z.string().max(300).nullable().optional(),
    utm_term: z.string().max(300).nullable().optional(),
    utm_campaign_id: z.string().max(120).nullable().optional(),
    utm_adset_id: z.string().max(120).nullable().optional(),
    utm_ad_id: z.string().max(120).nullable().optional(),
    fbclid: z.string().max(500).nullable().optional(),
    gclid: z.string().max(500).nullable().optional(),
    landing_path: z.string().max(1000).nullable().optional(),
    referrer: z.string().max(1000).nullable().optional(),
    fbp: z.string().max(200).nullable().optional(),
    fbc: z.string().max(500).nullable().optional(),
  })
  .partial()
  .optional()
  .nullable();

const ghlSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    email: z.string().trim().email().max(255),
    phone: z.string().trim().max(40).optional().nullable(),
    role: z.string().max(100).optional().nullable(),
    area: z.string().max(200).optional().nullable(),
    deals_12mo: z.string().max(50).optional().nullable(),
    lead_gen_sources: z.array(z.string().max(100)).max(20).optional().nullable(),
    website_status: z.string().max(200).optional().nullable(),
    website: z.string().max(500).optional().nullable(),
    followup_system: z.string().max(300).optional().nullable(),
    motivation: z.string().max(1000).optional().nullable(),
    lead_score: z.number().min(0).max(10).optional().nullable(),
    timezone: z.string().max(100).optional().nullable(),
    page_url: z.string().max(1000).optional().nullable(),
    user_agent: z.string().max(1000).optional().nullable(),
    variant: z.enum(["A", "B", "C", "D"]).optional().nullable(),
    attribution: attributionSchema,
    first_attribution: attributionSchema,
    survey: z
      .array(
        z.object({
          id: z.string().max(60),
          question: z.string().max(400),
          answer: z.union([z.string().max(1000), z.array(z.string().max(300)).max(20)]).nullable(),
        }),
      )
      .max(30)
      .optional()
      .nullable(),
    tier: z.string().max(10).optional().nullable(),
    routing_outcome: z.string().max(60).optional().nullable(),
  })
  .optional()
  .nullable();

const submitSchema = z.object({
  inquiry: inquirySchema,
  /** Scoring + routing snapshot; stored for every submission in the admin. */
  survey: surveyResponseSchema.optional().nullable(),
  audit: z.object({
    email: nullableText(320),
    name: nullableText(200),
    phone: nullableText(40),
    session_id: nullableText(100),
    path: nullableText(1000),
  }),
  ghl: ghlSchema,
  // Split-test attribution. Recorded server-side because the client redirects
  // immediately after submit, which cancels any in-flight browser inserts.
  tracking: z
    .object({
      variant_id: z.string().uuid().nullable().optional(),
      variant_label: z.enum(["A", "B", "C", "D"]).nullable().optional(),
      session_id: z.string().trim().min(8).max(120),
      visitor_id: z.string().trim().min(8).max(120),
    })
    .optional()
    .nullable(),

});

type SubmissionStatus = "attempt" | "success" | "failure";

export const submitSurveyLead = createServerFn({ method: "POST" })
  .inputValidator((input) => submitSchema.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const ua = getRequestHeader("user-agent") ?? null;
    let ip: string | null = null;
    try {
      ip = getRequestIP({ xForwardedFor: true }) ?? null;
    } catch {
      ip = null;
    }

    const writeAudit = async (
      stage: "inquiry_insert" | "ghl_sync",
      status: SubmissionStatus,
      error?: { code?: string | null; message?: string | null },
      metadata?: Record<string, unknown> | null,
    ) => {
      try {
        await supabaseAdmin.from("submission_attempts").insert({
          email: data.audit.email ?? data.inquiry.email,
          name: data.audit.name ?? data.inquiry.name,
          phone: data.audit.phone ?? data.inquiry.phone ?? null,
          stage,
          status,
          error_code: error?.code ?? null,
          error_message: error?.message ?? null,
          inquiry_id: stage === "inquiry_insert" && status === "success" ? data.inquiry.id : data.inquiry.id,
          session_id: data.audit.session_id ?? null,
          path: data.audit.path ?? null,
          user_agent: ua,
          ip,
          payload: (stage === "inquiry_insert" ? data.inquiry : data.ghl) as never,
          metadata: (metadata ?? null) as never,
        });
      } catch (auditError) {
        console.error("[submission] audit write failed", auditError);
      }
    };

    // 1) Insert inquiry — this is the durable server-side capture of the
    // lead. Even if every downstream integration fails, this row exists.
    // Last line of defence: if the browser sent no ad attribution at all,
    // recover it server-side from the cookie the worker banked on the landing
    // request, then from the submitting page's URL.
    if (!data.inquiry.utm_source && !data.inquiry.utm_content && !data.inquiry.utm_campaign && !data.inquiry.fbclid && !data.inquiry.gclid) {
      try {
        const { parseTrackingParams, SERVER_ATTR_COOKIE } = await import("./tracking-params");
        const cookieHeader = getRequestHeader("cookie") ?? "";
        const match = cookieHeader.match(new RegExp(`(?:^|; )${SERVER_ATTR_COOKIE}=([^;]*)`));
        let recovered: Record<string, string | null> = {};
        if (match) {
          try {
            recovered = JSON.parse(decodeURIComponent(match[1])) as Record<string, string | null>;
          } catch {
            recovered = {};
          }
        }
        if (!recovered.utm_source && !recovered.utm_content) {
          const referer = getRequestHeader("referer");
          if (referer) {
            const search = new URL(referer).search;
            recovered = { ...recovered, ...parseTrackingParams(search) };
          }
        }
        for (const k of [
          "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
          "fbclid", "gclid", "utm_campaign_id", "utm_adset_id", "utm_ad_id",
        ] as const) {
          const v = recovered[k];
          if (v && !data.inquiry[k]) data.inquiry[k] = v;
        }
        if (recovered.landing_path && !data.inquiry.landing_path) data.inquiry.landing_path = recovered.landing_path;
        if (recovered.first_utm_source && !data.inquiry.first_utm_source) data.inquiry.first_utm_source = recovered.first_utm_source;
        if (recovered.first_utm_medium && !data.inquiry.first_utm_medium) data.inquiry.first_utm_medium = recovered.first_utm_medium;
        if (recovered.first_utm_campaign && !data.inquiry.first_utm_campaign) data.inquiry.first_utm_campaign = recovered.first_utm_campaign;
        if (recovered.first_utm_content && !data.inquiry.first_utm_content) data.inquiry.first_utm_content = recovered.first_utm_content;
        if (recovered.first_utm_term && !data.inquiry.first_utm_term) data.inquiry.first_utm_term = recovered.first_utm_term;
        if (recovered.first_utm_campaign_id && !data.inquiry.first_utm_campaign_id) data.inquiry.first_utm_campaign_id = recovered.first_utm_campaign_id;
        if (recovered.first_utm_adset_id && !data.inquiry.first_utm_adset_id) data.inquiry.first_utm_adset_id = recovered.first_utm_adset_id;
        if (recovered.first_utm_ad_id && !data.inquiry.first_utm_ad_id) data.inquiry.first_utm_ad_id = recovered.first_utm_ad_id;
        if (recovered.first_fbclid && !data.inquiry.first_fbclid) data.inquiry.first_fbclid = recovered.first_fbclid;
        if (recovered.first_gclid && !data.inquiry.first_gclid) data.inquiry.first_gclid = recovered.first_gclid;
        if (recovered.first_landing_path && !data.inquiry.first_landing_path) data.inquiry.first_landing_path = recovered.first_landing_path;
        if (recovered.first_referrer && !data.inquiry.first_referrer) data.inquiry.first_referrer = recovered.first_referrer;
      } catch (recoverError) {
        console.error("[submission] attribution recovery failed", recoverError);
      }
    }

    await writeAudit("inquiry_insert", "attempt");
    const { error } = await supabaseAdmin.from("inquiries").insert(data.inquiry);
    if (error) {
      const message = `${error.message ?? "Unknown insert failure"}${error.details ? ` | ${error.details}` : ""}${error.hint ? ` | ${error.hint}` : ""}`.slice(0, 2000);
      await writeAudit("inquiry_insert", "failure", { code: error.code, message });
      console.error("[submission] failure", {
        email: data.inquiry.email,
        inquiry_id: data.inquiry.id,
        code: error.code,
        message,
      });
      return { ok: false as const, error: message };
    }
    await writeAudit("inquiry_insert", "success");
    console.log(`[submission] success stage=inquiry_insert email=${data.inquiry.email} inquiry_id=${data.inquiry.id}`);

    // 2) Run GHL sync inline (same server call). Doing this on the server
    // means a client-side redirect after the response cannot cancel the
    // upstream fetch to Go High Level. GHL failures never fail the
    // registration — the inquiry row is the lead-of-record fallback.
    let ghlResult: { ok: boolean; contactId?: string | null; error?: string } = { ok: false, error: "not attempted" };
    if (data.ghl) {
      await writeAudit("ghl_sync", "attempt");
      try {
        const { runGhlSync } = await import("./ghl.server");
        ghlResult = await runGhlSync(data.ghl, { inquiryId: data.inquiry.id });
      } catch (e) {
        ghlResult = { ok: false, error: (e as Error)?.message ?? "GHL sync exception" };
        console.error("[submission] ghl sync exception", e);
      }
      await writeAudit(
        "ghl_sync",
        ghlResult.ok ? "success" : "failure",
        ghlResult.ok ? undefined : { message: ghlResult.error ?? "GHL sync failed" },
        { contact_id: ghlResult.contactId ?? null },
      );
    }

    // 3) Record the split-test submission + completion event server-side.
    // Resolve the variant by label when the client lost the cached id, so a
    // lead is never left without a landing-page attribution.
    // Runs even when the client sent no tracking payload (blocked storage,
    // in-app browsers): every lead must produce exactly one completion row, or
    // Completions silently undercounts Leads.
    {
      try {
        let variantId = data.tracking?.variant_id ?? null;
        const label = data.tracking?.variant_label ?? data.ghl?.variant ?? null;
        if (!variantId && label) {
          const { data: v } = await supabaseAdmin
            .from("landing_page_variants")
            .select("id")
            .eq("variant_label", label)
            .maybeSingle();
          variantId = v?.id ?? null;
        }
        if (!variantId) {
          // Last resort: the currently active landing page variant.
          const { data: v } = await supabaseAdmin
            .from("landing_page_variants")
            .select("id")
            .eq("is_active", true)
            .order("traffic_percentage", { ascending: false })
            .limit(1)
            .maybeSingle();
          variantId = v?.id ?? null;
        }
        if (!variantId) {
          console.error("[submission] variant tracking skipped — unresolved variant", data.tracking);
        } else {
          const sessionId = data.tracking?.session_id ?? `lead-${data.inquiry.id}`;
          const { resolveVisitorId } = await import("./tracking.server");
          const visitorId = resolveVisitorId(data.tracking?.visitor_id ?? null);
          // Both writes are idempotent (unique indexes + ignoreDuplicates), so a
          // retried submission can never produce two leads in the funnel math.
          await supabaseAdmin
            .from("variant_submissions")
            .upsert(
              {
                variant_id: variantId,
                session_id: sessionId,
                inquiry_id: data.inquiry.id,
                contact_id: ghlResult.contactId ?? null,
              },
              { onConflict: "variant_id,session_id", ignoreDuplicates: true },
            );
          const { error: evErr } = await supabaseAdmin.from("variant_events").insert({
            variant_id: variantId,
            session_id: sessionId,
            event_type: "survey_complete",
            event_metadata: {
              inquiry_id: data.inquiry.id,
              visitor_id: visitorId,
              source: data.tracking ? "durable_submission" : "server_fallback",
            } as never,
          });
          // 23505 = the milestone idempotency index rejected a duplicate; that
          // is the intended behaviour on a retried submission.
          if (evErr && evErr.code !== "23505") {
            console.error("[submission] survey_complete event write failed", evErr);
          }
        }
      } catch (e) {
        console.error("[submission] variant tracking write failed", e);
      }
    }

    // 4) Survey response record — one row per submission (qualified, maybe and
    // disqualified alike) so lead quality can be tracked per landing page.
    if (data.survey) {
      try {
        await insertSurveyResponse(
          { ...data.survey, inquiry_id: data.inquiry.id },
          ua,
        );
      } catch (e) {
        console.error("[submission] survey response write failed", e);
      }
    }

    // 5) Meta Conversions API — "Lead" for QUALIFIED leads only.
    // Qualified = clinic owner + offers (or has the equipment for) the
    // treatment + care plan price of $3,000+  ==  routing Tier 3 / demo.
    const tier = data.survey?.tier ?? data.ghl?.tier ?? null;
    let capi: { ok: boolean; error?: string | null } = { ok: false, error: "not qualified" };
    if (tier === "3") {
      try {
        const { sendMetaCapiEvent } = await import("./meta-capi.server");
        const nameParts = (data.inquiry.name ?? "").trim().split(/\s+/);
        const result = await sendMetaCapiEvent({
          eventName: "Lead",
          eventId: data.inquiry.meta_event_id || `lead-${data.inquiry.id}`,
          eventSourceUrl: data.ghl?.page_url ?? data.audit.path ?? null,
          user: {
            email: data.inquiry.email,
            phone: data.inquiry.phone ?? null,
            firstName: nameParts[0] ?? null,
            lastName: nameParts.slice(1).join(" ") || null,
            fbp: data.inquiry.fbp ?? null,
            fbc: data.inquiry.fbc ?? null,
            clientIp: ip,
            userAgent: data.ghl?.user_agent ?? ua,
          },
          customData: {
            lead_tier: "3",
            routing_outcome: data.ghl?.routing_outcome ?? "demo",
            lead_score: data.inquiry.lead_score ?? undefined,
            variant: data.ghl?.variant ?? undefined,
            currency: "USD",
            value: 1,
          },
        });
        capi = { ok: result.ok, error: result.error ?? null };
      } catch (e) {
        capi = { ok: false, error: (e as Error)?.message ?? "CAPI exception" };
        console.error("[submission] meta capi exception", e);
      }
    }


    return {
      ok: true as const,
      inquiry_id: data.inquiry.id,
      qualified: tier === "3",
      capi: { ok: capi.ok, error: capi.error ?? null },
      ghl: {
        ok: ghlResult.ok,
        contactId: ghlResult.contactId ?? null,
        error: ghlResult.ok ? null : ghlResult.error ?? null,
      },
    };
  });
